import { z } from "zod";

export const billingCycleSchema = z.enum(["monthly", "annual"]);
export type BillingCycle = z.infer<typeof billingCycleSchema>;

export const planConfigItemSchema = z.object({
  monthlyPrice: z.number().nonnegative(),
  annualPrice: z.number().nonnegative(),
  label: z.string().min(1),
});

export const activeOfferSchema = z.object({
  enabled: z.boolean(),
  discountPercent: z.number().min(0).max(100),
  badgeText: z.string().default(""),
  title: z.string().default(""),
  description: z.string().default(""),
  validUntil: z.string().optional(),
});

export const bankDetailsConfigSchema = z.object({
  bankName: z.string().min(1),
  branch: z.string().min(1),
  accountName: z.string().min(1),
  accountNumber: z.string().min(1),
  instructions: z.string().optional(),
});

export const billingPlansConfigSchema = z.object({
  currency: z.enum(["LKR", "USD"]).default("LKR"),
  plans: z.object({
    starter: z.object({
      monthlyPrice: z.literal(0),
      annualPrice: z.literal(0),
      label: z.string().default("Starter"),
    }),
    pro: planConfigItemSchema,
    premium: planConfigItemSchema,
  }),
  activeOffer: activeOfferSchema.optional(),
  bankDetails: bankDetailsConfigSchema,
});

export type BillingPlansConfig = z.infer<typeof billingPlansConfigSchema>;

export const DEFAULT_BILLING_CONFIG: BillingPlansConfig = {
  currency: "LKR",
  plans: {
    starter: { monthlyPrice: 0, annualPrice: 0, label: "Starter" },
    pro: { monthlyPrice: 1500, annualPrice: 15000, label: "Pro" },
    premium: { monthlyPrice: 2000, annualPrice: 20000, label: "Premium" },
  },
  activeOffer: {
    enabled: false,
    discountPercent: 0,
    badgeText: "",
    title: "",
    description: "",
  },
  bankDetails: {
    bankName: "Commercial Bank of Ceylon",
    branch: "Colombo 01 (City Office)",
    accountName: "Profolio Solutions (Pvt) Ltd",
    accountNumber: "1000 4589 2310",
    instructions: "Please enter your username or registered email as the payment reference.",
  },
};

/**
 * Calculates effective price given billing configuration, requested tier, and billing cycle.
 * Takes active promotional discounts into account.
 */
export function calculateTierPrice(
  config: BillingPlansConfig,
  tier: "pro" | "premium",
  billingCycle: "monthly" | "annual" = "monthly"
): {
  baseAmount: number;
  discountedAmount: number;
  hasDiscount: boolean;
  discountPercent: number;
  formattedAmount: string;
  currency: string;
  itemName: string;
} {
  const plan = config.plans[tier];
  const baseAmount = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;

  let discountPercent = 0;
  if (config.activeOffer?.enabled && config.activeOffer.discountPercent > 0) {
    if (!config.activeOffer.validUntil || new Date(config.activeOffer.validUntil) > new Date()) {
      discountPercent = config.activeOffer.discountPercent;
    }
  }

  const discountedAmount =
    discountPercent > 0 ? Math.round(baseAmount * (1 - discountPercent / 100)) : baseAmount;

  return {
    baseAmount,
    discountedAmount,
    hasDiscount: discountPercent > 0,
    discountPercent,
    formattedAmount: discountedAmount.toFixed(2),
    currency: config.currency,
    itemName: `Profolio ${plan.label} (${billingCycle === "annual" ? "Annual" : "Monthly"})`,
  };
}
